import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
    Clock, MapPin, ChevronLeft, ChevronRight, ChevronDown, Trophy, User, Heart, HeartOff,
    Sparkles, Info, TrendingUp, Scale, Timer, AlertCircle, CheckCircle2, XCircle,
    Play, Flag, HelpCircle, Lightbulb, Target, Star, Award, Zap, ExternalLink, Bot, Users, Loader2
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO, isPast, isFuture, differenceInMinutes } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useBetSlip } from '../context/BetSlipContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getToken } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Experience Score Badge Component
const ExperienceScoreBadge = ({ runs, wins, compact = false }) => {
    if (!runs || runs === 0) return <Badge variant="outline" className="text-xs">Debut</Badge>;
    
    const winRate = Math.round((wins / runs) * 100);
    let level = 'Novice';
    let color = 'bg-slate-500';
    let icon = '🌱';
    
    if (runs >= 20) {
        level = 'Veteran';
        color = 'bg-purple-500';
        icon = '⭐';
    } else if (runs >= 10) {
        level = 'Experienced';
        color = 'bg-blue-500';
        icon = '💪';
    } else if (runs >= 5) {
        level = 'Developing';
        color = 'bg-emerald-500';
        icon = '📈';
    }
    
    if (compact) {
        return (
            <Tooltip>
                <TooltipTrigger>
                    <Badge className={`${color} text-white text-xs cursor-help`}>
                        {runs}R/{wins}W
                    </Badge>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="font-semibold">{level}</p>
                    <p>{runs} career runs, {wins} wins ({winRate}%)</p>
                </TooltipContent>
            </Tooltip>
        );
    }
    
    return (
        <div className="flex items-center gap-1">
            <Badge className={`${color} text-white`}>
                <Zap className="h-3 w-3 mr-1" />
                {level}
            </Badge>
            <span className="text-xs text-muted-foreground">
                {runs}R/{wins}W ({winRate}%)
            </span>
        </div>
    );
};

// Helper function to format going condition nicely
const formatGoing = (going) => {
    if (!going || going === 'Unknown') return 'Unknown';
    
    // Handle snake_case formats like "standard_slow", "standard_fast", "good_to_soft"
    const formatted = going
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    
    // Special cases
    const mappings = {
        'Standard Slow': 'Standard (Slow)',
        'Standard Fast': 'Standard (Fast)',
        'Good To Soft': 'Good to Soft',
        'Good To Firm': 'Good to Firm',
        'Soft To Heavy': 'Soft to Heavy',
    };
    
    return mappings[formatted] || formatted;
};

// Helper function to format distance
const formatDistance = (meta) => {
    // Try event:distance:string first (e.g., "2m 2 1/2f")
    const distanceString = meta?.['event:distance:string']?.value;
    if (distanceString) return distanceString;
    
    // Fallback to event:distance
    const distance = meta?.['event:distance']?.value;
    if (distance) return distance;
    
    // Try to construct from yards
    const yards = meta?.['event:distance:yards']?.value;
    if (yards) {
        const furlongs = Math.floor(yards / 220);
        const miles = Math.floor(furlongs / 8);
        const remainingFurlongs = furlongs % 8;
        
        if (miles > 0 && remainingFurlongs > 0) {
            return `${miles}m ${remainingFurlongs}f`;
        } else if (miles > 0) {
            return `${miles}m`;
        } else {
            return `${furlongs}f`;
        }
    }
    
    return 'TBC';
};

// Enhanced Glossary for tooltips with beginner-friendly explanations
const GLOSSARY = {
    'OR': {
        title: 'Official Rating',
        short: 'A score showing how good the horse is',
        detail: 'Higher numbers mean better horses. A horse rated 140 is much better than one rated 100. The rating is based on past race performances.'
    },
    'ADJ': {
        title: 'Adjusted Rating',
        short: 'Modified rating accounting for current form',
        detail: 'The official rating adjusted by analysts based on recent performances, weight changes, or other factors. May be higher or lower than OR.'
    },
    'TS': {
        title: 'Timeform Star Rating',
        short: 'Quality indicator from 1-5 stars',
        detail: '5 stars = top class, 4 stars = very good, 3 stars = above average, 2 stars = average, 1 star = below average. Based on Timeform\'s expert analysis.'
    },
    'Form': {
        title: 'Recent Results',
        short: 'How the horse has performed recently',
        detail: '1 = Won, 2 = Second place, 3 = Third. F = Fell, P = Pulled up (stopped). Recent good form (low numbers) is a positive sign!'
    },
    'Weight': {
        title: 'Carried Weight',
        short: 'How much the horse carries (jockey + equipment)',
        detail: 'Shown as stones-pounds (e.g., 11-4 = 11 stone 4 pounds). Better horses often carry more weight to level the playing field.'
    },
    'Going': {
        title: 'Ground Conditions',
        short: 'How soft or firm the track is',
        detail: 'Firm = hard ground, Good = ideal, Soft/Heavy = wet and muddy. Some horses prefer specific conditions - check their past races!'
    },
    'Draw': {
        title: 'Starting Position',
        short: 'Where the horse starts on flat races',
        detail: 'On some tracks, certain draw positions have advantages. Less relevant for jumps racing.'
    },
    'Class': {
        title: 'Race Quality',
        short: 'How competitive the race is',
        detail: 'Class 1 is the highest level (best horses), Class 7 is the lowest. Horses dropping in class may have an advantage.'
    }
};

const GlossaryTooltip = ({ term, children }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 cursor-help border-b border-dashed border-muted-foreground/50 hover:border-primary transition-colors">
                    {children}
                    <HelpCircle className="h-3 w-3 text-muted-foreground" />
                </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs p-3">
                <p className="font-semibold text-primary mb-1">{GLOSSARY[term]?.title || term}</p>
                <p className="text-sm font-medium mb-1">{GLOSSARY[term]?.short}</p>
                <p className="text-xs text-muted-foreground">{GLOSSARY[term]?.detail}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

// Race Status Badge with clear indicators
const RaceStatusBadge = ({ status, startTime }) => {
    const start = parseISO(startTime);
    const now = new Date();
    const minsToRace = differenceInMinutes(start, now);
    
    // Determine display status
    let displayStatus = 'upcoming';
    let label = 'Upcoming';
    let variant = 'secondary';
    let icon = Clock;
    
    if (status?.includes('WEIGHED_IN') || status?.includes('RESULT')) {
        displayStatus = 'finished';
        label = 'Result';
        variant = 'outline';
        icon = CheckCircle2;
    } else if (status?.includes('RUNNING') || status?.includes('GOING_DOWN') || status?.includes('GOING_BEHIND')) {
        displayStatus = 'live';
        label = 'LIVE';
        variant = 'destructive';
        icon = Play;
    } else if (minsToRace <= 5 && minsToRace > 0) {
        displayStatus = 'imminent';
        label = 'Starting Soon';
        variant = 'default';
        icon = AlertCircle;
    } else if (minsToRace <= 0 && !status?.includes('WEIGHED_IN')) {
        displayStatus = 'live';
        label = 'In Progress';
        variant = 'destructive';
        icon = Play;
    }
    
    const Icon = icon;
    
    return (
        <Badge 
            variant={variant} 
            className={`gap-1 ${displayStatus === 'live' ? 'animate-pulse' : ''}`}
            data-testid="race-status-badge"
        >
            <Icon className="h-3 w-3" />
            {label}
        </Badge>
    );
};

// Quick Results Card - Only shown for finished races
const QuickResultsCard = ({ race, entrants }) => {
    // Check if race is finished
    const isFinished = race.status?.includes('WEIGHED_IN') || race.status?.includes('RESULT');
    
    if (!isFinished) return null;
    
    // Sort entrants by finishing position
    const finishers = [...entrants]
        .filter(e => e.result?.position)
        .sort((a, b) => (a.result?.position || 99) - (b.result?.position || 99))
        .slice(0, 4);
    
    if (finishers.length === 0) return null;
    
    // Get dividends
    const dividends = race.dividends || [];
    const winDividend = dividends.find(d => d.type === 'tote-win');
    const placeDividends = dividends.filter(d => d.type === 'tote-place');
    
    const getPositionStyle = (pos) => {
        if (pos === 1) return 'bg-amber-500 text-amber-950';
        if (pos === 2) return 'bg-slate-400 text-slate-950';
        if (pos === 3) return 'bg-amber-700 text-amber-100';
        return 'bg-muted text-muted-foreground';
    };
    
    const getPositionLabel = (pos) => {
        if (pos === 1) return '1st';
        if (pos === 2) return '2nd';
        if (pos === 3) return '3rd';
        return `${pos}th`;
    };

    return (
        <Card className="mb-6 border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent" data-testid="quick-results-card">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    Race Result
                    <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-600">Official</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {/* Podium Display */}
                <div className="space-y-3 mb-4">
                    {finishers.map((horse, idx) => {
                        const pos = horse.result?.position;
                        const jockey = horse.participants?.find(p => p.participant?.type === 'jockey')?.participant?.name || 'Unknown';
                        // Use Starting Price (SP) - the official price at race start, not show prices
                        const odds = horse.betting?.startingprice?.price;
                        const beaten = horse.meta?.['entrant:beatendistance']?.value;
                        
                        // Get closeupcomment for race running commentary
                        const closeupComment = horse.contentitems?.find(
                            c => c.profile === 'closeupcomment'
                        )?.bodies?.en_GB;
                        
                        return (
                            <div 
                                key={horse.id}
                                className={`p-3 rounded-lg ${pos === 1 ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-muted/50'}`}
                            >
                                <div className="flex items-center gap-4">
                                    {/* Position Badge */}
                                    <div className={`result-position ${getPositionStyle(pos)} w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0`}>
                                        {pos}
                                    </div>
                                    
                                    {/* Horse Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{horse.name}</span>
                                            {pos === 1 && <Trophy className="h-4 w-4 text-amber-500" />}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {jockey}
                                            {beaten && pos > 1 && <span className="ml-2">• beaten {beaten}L</span>}
                                        </p>
                                    </div>
                                    
                                    {/* Odds */}
                                    <div className="text-right shrink-0">
                                        <span className="font-data font-semibold text-lg">
                                            {odds ? `${odds.numerator}/${odds.denominator}` : 'SP'}
                                        </span>
                                        {pos === 1 && winDividend && (
                                            <p className="text-xs text-muted-foreground">
                                                Tote: £{winDividend.dividend.toFixed(2)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Closeup Comment - Race running commentary */}
                                {closeupComment && pos <= 3 && (
                                    <div className="mt-2 pt-2 border-t border-border/30 ml-14">
                                        <p className="text-xs text-muted-foreground leading-relaxed italic">
                                            "{closeupComment}"
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* Dividends Summary */}
                {dividends.length > 0 && (
                    <div className="pt-3 border-t border-border/50">
                        <p className="text-xs text-muted-foreground mb-2 font-medium">Tote Returns (to £1 stake)</p>
                        <div className="flex flex-wrap gap-3 text-sm">
                            {winDividend && (
                                <span className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Win:</span>
                                    <span className="font-data font-semibold">£{winDividend.dividend.toFixed(2)}</span>
                                </span>
                            )}
                            {placeDividends.slice(0, 3).map((pd, idx) => (
                                <span key={idx} className="flex items-center gap-1">
                                    <span className="text-muted-foreground">Place {idx + 1}:</span>
                                    <span className="font-data font-semibold">£{pd.dividend.toFixed(2)}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Race Summary */}
                {race.finished && (
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50">
                        Race finished at {format(parseISO(race.finished), 'HH:mm')} • 
                        Off time: {race.offtime && format(parseISO(race.offtime), 'HH:mm')}
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// Form String with explanations
const FormString = ({ form, showLegend = false }) => {
    if (!form) return <span className="text-muted-foreground">No recent form</span>;
    
    const getFormClass = (char) => {
        if (char === '1') return 'bg-emerald-500 text-white';
        if (char === '2') return 'bg-emerald-400 text-white';
        if (char === '3') return 'bg-emerald-300 text-emerald-900';
        if (char.toUpperCase() === 'F' || char.toUpperCase() === 'P' || char.toUpperCase() === 'U') {
            return 'bg-red-500 text-white';
        }
        return 'bg-muted text-muted-foreground';
    };

    const getFormLabel = (char) => {
        if (char === '1') return 'Won';
        if (char === '2') return '2nd';
        if (char === '3') return '3rd';
        if (char.toUpperCase() === 'F') return 'Fell';
        if (char.toUpperCase() === 'P') return 'Pulled up';
        if (char.toUpperCase() === 'U') return 'Unseated';
        if (char === '0') return '10th+';
        return `${char}th`;
    };
    
    return (
        <div className="space-y-1">
            <div className="flex gap-1">
                {form.split('').slice(0, 6).map((char, idx) => (
                    <TooltipProvider key={idx}>
                        <Tooltip>
                            <TooltipTrigger>
                                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded ${getFormClass(char)}`}>
                                    {char}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{getFormLabel(char)} (race {idx + 1} ago)</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ))}
            </div>
            {showLegend && (
                <p className="text-xs text-muted-foreground">
                    Most recent race on the right →
                </p>
            )}
        </div>
    );
};

// Jockey Silk Image
const JockeySilk = ({ associations, silkDescription }) => {
    const silkAssoc = associations?.find(a => a.profile === 'jockeysilk');
    const silkUrl = silkAssoc?.renditions?.original?.href;
    const description = silkAssoc?.renditions?.original?.sourceText || silkDescription;
    
    if (!silkUrl) {
        return (
            <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                <User className="h-6 w-6 text-muted-foreground" />
            </div>
        );
    }
    
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger>
                    <div className="w-12 h-12 rounded overflow-hidden bg-white border">
                        <img 
                            src={silkUrl} 
                            alt="Jockey silks" 
                            className="w-full h-full object-contain"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-muted"><svg class="h-6 w-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                            }}
                        />
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="text-xs max-w-48">{description || 'Jockey silks'}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// Timeform Star Rating Display
const TimeformStars = ({ rating }) => {
    if (!rating && rating !== 0) return <span className="text-muted-foreground text-xs">-</span>;
    
    const numStars = parseInt(rating) || 0;
    const maxStars = 5;
    
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="flex items-center gap-0.5 cursor-help">
                        {[...Array(maxStars)].map((_, i) => (
                            <Star 
                                key={i} 
                                className={`h-3 w-3 ${i < numStars ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
                            />
                        ))}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="font-semibold">Timeform Rating: {numStars}/5</p>
                    <p className="text-xs text-muted-foreground">
                        {numStars >= 5 ? 'Top class performer' :
                         numStars >= 4 ? 'Very good horse' :
                         numStars >= 3 ? 'Above average' :
                         numStars >= 2 ? 'Average performer' :
                         'Below average'}
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// Expert Tip Badge
const ExpertTipBadge = ({ tipType }) => {
    if (!tipType) return null;
    
    const tips = {
        'tip:pick:selection': { label: 'Top Pick', icon: Star, className: 'bg-amber-500/20 text-amber-600 border-amber-500/30' },
        'tip:pick:alternative': { label: 'Each-Way', icon: Target, className: 'bg-blue-500/20 text-blue-600 border-blue-500/30' },
        'tip:pick:danger': { label: 'Dark Horse', icon: TrendingUp, className: 'bg-purple-500/20 text-purple-600 border-purple-500/30' }
    };
    
    const tip = tips[tipType];
    if (!tip) return null;
    
    const Icon = tip.icon;
    
    return (
        <Badge variant="outline" className={`gap-1 ${tip.className}`}>
            <Icon className="h-3 w-3" />
            {tip.label}
        </Badge>
    );
};

// Plain English Commentary Card
const CommentaryCard = ({ contentItems }) => {
    const pinsticker = contentItems?.find(item => 
        item.profile === 'pinstickerlong' || item.profile === 'pinstickershort'
    );
    const commentary = pinsticker?.bodies?.en_GB;
    
    if (!commentary) return null;
    
    return (
        <div className="mt-3 p-3 bg-muted/50 rounded-lg border-l-2 border-primary/50">
            <div className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                    {commentary}
                </p>
            </div>
        </div>
    );
};

const OddsButton = ({ odds, onClick, selected, disabled }) => {
    if (!odds) return <span className="text-muted-foreground text-sm">N/A</span>;
    
    const decimal = odds.price?.decimal || odds.decimal;
    const numerator = odds.price?.numerator || odds.numerator;
    const denominator = odds.price?.denominator || odds.denominator;
    const fractional = `${numerator}/${denominator}`;

    // Odds interpretation for beginners
    const getOddsHint = () => {
        if (decimal < 2) return 'Strong favourite';
        if (decimal < 4) return 'Well fancied';
        if (decimal < 8) return 'Decent chance';
        if (decimal < 15) return 'Outside chance';
        return 'Long shot';
    };

    if (disabled) {
        return (
            <div className="odds-btn opacity-50 cursor-not-allowed" data-testid="odds-btn-disabled">
                {fractional}
                <span className="text-[10px] block text-red-400">Started</span>
            </div>
        );
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={onClick}
                        className={`odds-btn ${selected ? 'selected' : ''}`}
                        data-testid="odds-btn"
                    >
                        {fractional}
                    </button>
                </TooltipTrigger>
                <TooltipContent>
                    <p className="font-semibold">{getOddsHint()}</p>
                    <p className="text-xs text-muted-foreground">
                        £10 bet returns £{(10 * decimal).toFixed(2)}
                    </p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const RunnerRow = ({ runner, eventInfo, stageName, onAddToStable, stableItems = [], tipType, expanded = false, careerStats = null, aiPicks = null }) => {
    const { addSelection, selections } = useBetSlip();
    const { isAuthenticated } = useAuth();
    const [showDetails, setShowDetails] = useState(expanded);

    // AI pick flags for this runner (same matching as the competition pick modal)
    const isAiTopPick   = aiPicks?.top_pick?.name === runner.name;
    const isAiEachWay   = aiPicks?.each_way?.name === runner.name;
    const isAiDarkHorse = aiPicks?.dark_horse?.name === runner.name;
    const aiReasoning =
        (isAiTopPick && aiPicks?.top_pick?.reasoning) ||
        (isAiEachWay && aiPicks?.each_way?.reasoning) ||
        (isAiDarkHorse && aiPicks?.dark_horse?.reasoning) || null;
    
    // Check if race has started
    const now = new Date();
    const raceStartTime = eventInfo?.start ? new Date(eventInfo.start) : null;
    const hasRaceStarted = raceStartTime && raceStartTime <= now;
    
    const meta = runner.meta || {};
    
    // Extract data from Podium API structure
    const weightPounds = meta['entrant:weight:allocation:pounds']?.value;
    const weight = weightPounds 
        ? `${Math.floor(weightPounds/14)}-${weightPounds%14}`
        : (meta['entrant:weight']?.value || '-');
    
    const form = meta['entrant:formfigures:jumpsix']?.figures 
        || meta['entrant:formfigures:flatsix']?.figures
        || meta['entrant:form']?.value || '';
    
    const participants = runner.participants || [];
    const jockeyParticipant = participants.find(p => p.participant?.type === 'jockey');
    const trainerParticipant = participants.find(p => p.participant?.type === 'trainer');
    const jockey = jockeyParticipant?.participant?.name || meta['entrant:jockey']?.value || 'Unknown';
    const trainer = trainerParticipant?.participant?.name || meta['entrant:trainer']?.value || 'Unknown';
    const jockeyId = jockeyParticipant?.participant?.id || `jockey-${jockey.replace(/\s+/g, '-').toLowerCase()}`;
    const trainerId = trainerParticipant?.participant?.id || `trainer-${trainer.replace(/\s+/g, '-').toLowerCase()}`;
    
    const orRating = runner.ratings?.find(r => r.code === 'entrant:rating:official')?.value
        || runner.ratings?.find(r => r.code === 'entrant:rating:or')?.value
        || meta['entrant:or']?.value || '-';
    
    // Extract additional ratings
    const adjustedRating = runner.ratings?.find(r => r.code === 'entrant:rating:adjusted')?.value || null;
    const timeformStar = runner.ratings?.find(r => r.code === 'entrant:rating:timeform:star')?.value || null;
    
    // Get jockey silks description from content items
    const silkDescription = runner.contentitems?.find(i => i.profile === 'jockeycolours')?.bodies?.en_GB;
    
    const latestOdds = runner.betting?.shows?.[0] || runner.betting?.forecast;
    const isSelected = selections.some(s => s.entrant_id === runner.id);
    const isNonRunner = runner.status === 'NON_RUNNER' || runner.status === 'DOUBTFUL' || meta['entrant:status']?.value === 'NonRunner' || meta['entrant:status']?.value === 'Doubtful';
    
    // Check if items are in stable
    const isHorseInStable = stableItems.some(s => s.item_id === runner.id && s.item_type === 'horse');
    const isJockeyInStable = stableItems.some(s => s.item_name === jockey && s.item_type === 'jockey');
    const isTrainerInStable = stableItems.some(s => s.item_name === trainer && s.item_type === 'trainer');

    const handleOddsClick = () => {
        if (!latestOdds || isNonRunner || hasRaceStarted) return;
        
        const decimal = latestOdds.price?.decimal || latestOdds.decimal;
        const numerator = latestOdds.price?.numerator || latestOdds.numerator;
        const denominator = latestOdds.price?.denominator || latestOdds.denominator;
        
        addSelection({
            entrant_id: runner.id,
            entrant_name: runner.name,
            event_id: eventInfo.id,
            event_name: eventInfo.title,
            stage_name: stageName,
            odds_decimal: decimal,
            odds_fractional: `${numerator}/${denominator}`,
            start_time: eventInfo.start,
            race_time: eventInfo.start
        });
    };

    return (
        <TooltipProvider>
        <div 
            className={`runner-row p-4 border-b last:border-b-0 ${isNonRunner ? 'opacity-50' : ''}`}
            data-testid={`runner-row-${runner.id}`}
        >
            <div className="flex items-start gap-3">
                {/* Jockey Silk */}
                <JockeySilk associations={runner.associations} silkDescription={silkDescription} />

                {/* Runner Number */}
                <div className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-data text-sm font-bold">
                        {runner.entrantnumber || '?'}
                    </div>
                </div>

                {/* Runner Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Link 
                            to={`/horse/${runner.id}`}
                            className="font-heading font-semibold text-lg hover:text-primary transition-colors inline-flex items-center gap-1 group"
                        >
                            {runner.name}
                            <ExternalLink className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                        {isNonRunner && <Badge variant="destructive">Non Runner</Badge>}
                        {careerStats && (
                            <ExperienceScoreBadge 
                                runs={careerStats.runs} 
                                wins={careerStats.wins} 
                                compact={true}
                            />
                        )}
                        <ExpertTipBadge tipType={tipType} />
                        {isAiTopPick && (
                            <Badge className="bg-rose-500/15 text-rose-500 border-rose-500/30 gap-1" variant="outline">
                                <Sparkles className="h-3 w-3" /> AI Top Pick
                            </Badge>
                        )}
                        {isAiEachWay && (
                            <Badge className="bg-cyan-500/15 text-cyan-600 border-cyan-500/30 gap-1" variant="outline">
                                <Sparkles className="h-3 w-3" /> AI E/W
                            </Badge>
                        )}
                        {isAiDarkHorse && (
                            <Badge className="bg-violet-500/15 text-violet-500 border-violet-500/30 gap-1" variant="outline">
                                <Sparkles className="h-3 w-3" /> AI Dark Horse
                            </Badge>
                        )}
                        <button
                            onClick={() => onAddToStable({ id: runner.id, name: runner.name, type: 'horse' })}
                            className="ml-auto text-muted-foreground hover:text-accent transition-colors"
                            data-testid={`stable-btn-${runner.id}`}
                            title={isHorseInStable ? 'Remove from stable' : 'Add to stable'}
                        >
                            {isHorseInStable ? (
                                <Heart className="h-5 w-5 fill-accent text-accent" />
                            ) : (
                                <HeartOff className="h-5 w-5" />
                            )}
                        </button>
                    </div>

                    {/* Key Info Row with trackable jockey/trainer */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-sm mb-2">
                        <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">J: </span>
                            <span className="font-medium">{jockey}</span>
                            {jockey !== 'Unknown' && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddToStable({ id: jockeyId, name: jockey, type: 'jockey' });
                                    }}
                                    className="text-muted-foreground hover:text-blue-500 transition-colors ml-1"
                                    title={isJockeyInStable ? 'Remove jockey from stable' : 'Track this jockey'}
                                >
                                    <Heart className={`h-3.5 w-3.5 ${isJockeyInStable ? 'fill-blue-500 text-blue-500' : ''}`} />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">T: </span>
                            <span className="font-medium">{trainer}</span>
                            {trainer !== 'Unknown' && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddToStable({ id: trainerId, name: trainer, type: 'trainer' });
                                    }}
                                    className="text-muted-foreground hover:text-purple-500 transition-colors ml-1"
                                    title={isTrainerInStable ? 'Remove trainer from stable' : 'Track this trainer'}
                                >
                                    <Heart className={`h-3.5 w-3.5 ${isTrainerInStable ? 'fill-purple-500 text-purple-500' : ''}`} />
                                </button>
                            )}
                        </div>
                        <div>
                            <GlossaryTooltip term="Weight">
                                <span className="text-muted-foreground">Wgt</span>
                            </GlossaryTooltip>
                            <span className="font-medium ml-1">{weight}</span>
                        </div>
                        <div>
                            <GlossaryTooltip term="OR">
                                <span className="text-muted-foreground">OR</span>
                            </GlossaryTooltip>
                            <span className="font-data font-semibold ml-1">{orRating}</span>
                        </div>
                    </div>

                    {/* Ratings Row - Additional ratings display */}
                    {(adjustedRating || timeformStar) && (
                        <div className="flex items-center gap-4 text-sm mb-2 flex-wrap">
                            {adjustedRating && (
                                <div className="flex items-center gap-1" data-testid="adjusted-rating">
                                    <GlossaryTooltip term="ADJ">
                                        <span className="text-muted-foreground">Adj</span>
                                    </GlossaryTooltip>
                                    <span className={`font-data font-semibold ${
                                        parseInt(adjustedRating) > parseInt(orRating) 
                                            ? 'text-emerald-500' 
                                            : parseInt(adjustedRating) < parseInt(orRating) 
                                                ? 'text-red-400' 
                                                : ''
                                    }`}>
                                        {adjustedRating}
                                        {orRating !== '-' && adjustedRating !== orRating && (
                                            <span className="text-xs ml-0.5">
                                                ({parseInt(adjustedRating) > parseInt(orRating) ? '+' : ''}{parseInt(adjustedRating) - parseInt(orRating)})
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {timeformStar && (
                                <div className="flex items-center gap-1" data-testid="timeform-rating">
                                    <GlossaryTooltip term="TS">
                                        <span className="text-muted-foreground">TS</span>
                                    </GlossaryTooltip>
                                    <TimeformStars rating={timeformStar} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Form */}
                    <div className="flex items-center gap-4">
                        <div>
                            <GlossaryTooltip term="Form">
                                <span className="text-xs text-muted-foreground uppercase">Form</span>
                            </GlossaryTooltip>
                            <div className="mt-1">
                                <FormString form={form} />
                            </div>
                        </div>
                    </div>

                    {/* Expandable Commentary + AI reasoning */}
                    {(runner.contentitems?.length > 0 || aiReasoning) && (
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                        >
                            <Lightbulb className="h-3 w-3" />
                            {showDetails ? 'Hide analysis' : (aiReasoning ? 'Show analysis' : 'Show expert analysis')}
                        </button>
                    )}

                    {showDetails && <CommentaryCard contentItems={runner.contentitems} />}

                    {/* AI pick reasoning — same breakdown the competition pick modal shows */}
                    {showDetails && isAiTopPick && aiPicks?.top_pick?.reasoning && (
                        <div className="mt-2 p-2.5 bg-rose-500/5 border border-rose-500/20 rounded-lg text-xs sm:text-sm leading-relaxed">
                            <span className="font-semibold text-rose-500">Why AI Top Pick:</span> {aiPicks.top_pick.reasoning}
                        </div>
                    )}
                    {showDetails && isAiEachWay && aiPicks?.each_way?.reasoning && (
                        <div className="mt-2 p-2.5 bg-cyan-500/5 border border-cyan-500/20 rounded-lg text-xs sm:text-sm leading-relaxed">
                            <span className="font-semibold text-cyan-600">Why AI E/W:</span> {aiPicks.each_way.reasoning}
                        </div>
                    )}
                    {showDetails && isAiDarkHorse && aiPicks?.dark_horse?.reasoning && (
                        <div className="mt-2 p-2.5 bg-violet-500/5 border border-violet-500/20 rounded-lg text-xs sm:text-sm leading-relaxed">
                            <span className="font-semibold text-violet-500">Why AI Dark Horse:</span> {aiPicks.dark_horse.reasoning}
                        </div>
                    )}
                    {showDetails && aiReasoning && aiPicks?.overall_analysis && (
                        <div className="mt-2 p-2.5 bg-purple-500/5 border border-purple-500/20 rounded-lg text-xs sm:text-sm leading-relaxed">
                            <span className="font-semibold text-purple-500">AI Analysis:</span> {aiPicks.overall_analysis}
                        </div>
                    )}
                </div>

                {/* Odds */}
                <div className="text-right">
                    <span className="text-xs text-muted-foreground block mb-1">Odds</span>
                    {!isNonRunner ? (
                        <OddsButton 
                            odds={latestOdds} 
                            onClick={handleOddsClick}
                            selected={isSelected}
                            disabled={hasRaceStarted}
                        />
                    ) : (
                        <span className="text-muted-foreground text-sm">N/R</span>
                    )}
                </div>
            </div>
        </div>
        </TooltipProvider>
    );
};

// Expert Tips Summary Card
const ExpertTipsCard = ({ tips, entrants }) => {
    if (!tips || tips.length === 0) return null;
    
    const getEntrantName = (tipEntrant) => {
        // Try to match by name or ID
        const entrant = entrants?.find(e => 
            e.name === tipEntrant?.name || 
            e.altids?.some(aid => tipEntrant?.id?.includes(aid))
        );
        return entrant?.name || tipEntrant?.name || 'Unknown';
    };
    
    const selection = tips.find(t => t.type === 'tip:pick:selection');
    const alternative = tips.find(t => t.type === 'tip:pick:alternative');
    const danger = tips.find(t => t.type === 'tip:pick:danger');
    
    return (
        <Card className="border-primary/30 bg-primary/5" data-testid="expert-tips-card">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="h-5 w-5 text-primary" />
                    Podium Picks
                    <Badge variant="secondary" className="ml-2">Tips</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                    Our Podium analysts have analyzed this race. Here are their selections:
                </p>
                
                {selection && (
                    <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                        <Star className="h-5 w-5 text-amber-500" />
                        <div>
                            <p className="font-semibold">{getEntrantName(selection.entrant)}</p>
                            <p className="text-xs text-muted-foreground">Top Pick - Best chance of winning</p>
                        </div>
                    </div>
                )}
                
                {alternative && (
                    <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <Target className="h-5 w-5 text-blue-500" />
                        <div>
                            <p className="font-semibold">{getEntrantName(alternative.entrant)}</p>
                            <p className="text-xs text-muted-foreground">Each-Way Value - Good for place bets</p>
                        </div>
                    </div>
                )}
                
                {danger && (
                    <div className="flex items-center gap-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                        <TrendingUp className="h-5 w-5 text-purple-500" />
                        <div>
                            <p className="font-semibold">{getEntrantName(danger.entrant)}</p>
                            <p className="text-xs text-muted-foreground">Dark Horse - Could cause an upset</p>
                        </div>
                    </div>
                )}
                
                <p className="text-xs text-muted-foreground pt-2 border-t">
                    💡 <strong>Tip:</strong> Podium picks aren't always right! Use these as a starting point for your own research.
                </p>
            </CardContent>
        </Card>
    );
};

// Combined Expert vs AI Picks Card
const ExpertsVsAICard = ({ tips, entrants, eventId }) => {
    const [aiPicks, setAiPicks] = useState(null);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated } = useAuth();
    
    useEffect(() => {
        const fetchAIPicks = async () => {
            if (!eventId) return;
            try {
                const token = getToken();
                const res = await axios.get(`${API}/ai-picks/${eventId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.available) {
                    setAiPicks(res.data.data.picks);
                }
            } catch (err) {
                console.error('Failed to fetch AI picks:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAIPicks();
    }, [eventId]);
    
    const getEntrantName = (tipEntrant) => {
        const entrant = entrants?.find(e => 
            e.name === tipEntrant?.name || 
            e.altids?.some(aid => tipEntrant?.id?.includes(aid))
        );
        return entrant?.name || tipEntrant?.name || 'Unknown';
    };
    
    const selection = tips?.find(t => t.type === 'tip:pick:selection');
    const alternative = tips?.find(t => t.type === 'tip:pick:alternative');
    const danger = tips?.find(t => t.type === 'tip:pick:danger');
    
    const hasExpertPicks = selection || alternative || danger;
    const hasAIPicks = aiPicks && (aiPicks.top_pick || aiPicks.each_way || aiPicks.dark_horse);
    
    if (!hasExpertPicks && !hasAIPicks && !loading) return null;
    
    const PickRow = ({ label, icon, expertPick, aiPick, bgColor, borderColor }) => (
        <div className={`p-3 ${bgColor} rounded-lg border ${borderColor}`}>
            <div className="flex items-center justify-center gap-2 mb-2">
                {icon}
                <span className="font-medium text-sm">{label}</span>
            </div>
            <div className="grid grid-cols-[1fr,auto,1fr] gap-2 items-center">
                <div className="text-center">
                    <p className="font-semibold">{expertPick || '-'}</p>
                </div>
                <div className="text-center">
                    <span className="text-xs font-bold text-muted-foreground px-2">vs</span>
                </div>
                <div className="text-center">
                    <p className="font-semibold">{aiPick || '-'}</p>
                </div>
            </div>
        </div>
    );
    
    return (
        <Card className="border-primary/30 bg-gradient-to-r from-blue-500/5 to-purple-500/5" data-testid="experts-vs-ai-card">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-primary" />
                        Race Picks
                    </div>
                    <Badge variant="outline" className="text-xs">Side by Side</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Header Row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-2 bg-blue-500/10 rounded-lg">
                        <div className="flex items-center justify-center gap-2">
                            <Users className="h-4 w-4 text-blue-500" />
                            <span className="font-semibold text-blue-500">Podium</span>
                        </div>
                    </div>
                    <div className="text-center p-2 bg-purple-500/10 rounded-lg">
                        <div className="flex items-center justify-center gap-2">
                            <Bot className="h-4 w-4 text-purple-500" />
                            <span className="font-semibold text-purple-500">AI</span>
                        </div>
                    </div>
                </div>
                
                {loading ? (
                    <div className="space-y-2">
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-16 w-full" />
                        <Skeleton className="h-16 w-full" />
                    </div>
                ) : (
                    <>
                        <PickRow 
                            label="Top Pick"
                            icon={<Star className="h-4 w-4 text-amber-500" />}
                            expertPick={selection ? getEntrantName(selection.entrant) : null}
                            aiPick={aiPicks?.top_pick?.name}
                            bgColor="bg-amber-500/10"
                            borderColor="border-amber-500/20"
                        />
                        
                        <PickRow 
                            label="Each-Way Value"
                            icon={<Target className="h-4 w-4 text-blue-500" />}
                            expertPick={alternative ? getEntrantName(alternative.entrant) : null}
                            aiPick={aiPicks?.each_way?.name}
                            bgColor="bg-blue-500/10"
                            borderColor="border-blue-500/20"
                        />
                        
                        <PickRow 
                            label="Dark Horse"
                            icon={<Zap className="h-4 w-4 text-purple-500" />}
                            expertPick={danger ? getEntrantName(danger.entrant) : null}
                            aiPick={aiPicks?.dark_horse?.name}
                            bgColor="bg-purple-500/10"
                            borderColor="border-purple-500/20"
                        />
                        
                        {aiPicks?.overall_analysis && (
                            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                                <p className="text-xs text-muted-foreground">
                                    <Bot className="h-3 w-3 inline mr-1" />
                                    <strong>AI Analysis:</strong> {aiPicks.overall_analysis}
                                </p>
                            </div>
                        )}
                    </>
                )}
                
                <p className="text-xs text-muted-foreground pt-2 border-t">
                    💡 Compare Podium tips with AI predictions. Track performance on the <Link to="/experts-vs-ai" className="text-primary underline">Podium vs AI</Link> page.
                </p>
            </CardContent>
        </Card>
    );
};

const AIInsightCard = ({ eventInfo, entrants, userSettings }) => {
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated } = useAuth();

    const generateInsight = async () => {
        if (!isAuthenticated) {
            toast.error('Please login to generate AI insights');
            return;
        }
        
        setLoading(true);
        try {
            const response = await axios.post(`${API}/racing/insights`, {
                event_id: eventInfo.id,
                event_info: {
                    id: eventInfo.id,
                    title: eventInfo.title,
                    venue: eventInfo.venue || 'Unknown',
                    distance: eventInfo.meta?.['event:distance']?.value || 'Unknown',
                    going: formatGoing(eventInfo.meta?.['event:going']?.value),
                    class: eventInfo.meta?.['event:class']?.value || 'Unknown'
                },
                entrants: entrants.map(e => ({
                    name: e.name,
                    form: e.meta?.['entrant:formfigures:jumpsix']?.figures || e.meta?.['entrant:form']?.value,
                    weight: e.meta?.['entrant:weight:allocation:pounds']?.value,
                    jockey: e.participants?.find(p => p.participant?.type === 'jockey')?.participant?.name,
                    trainer: e.participants?.find(p => p.participant?.type === 'trainer')?.participant?.name,
                    odds: e.betting?.shows?.[0]?.price?.decimal || e.betting?.forecast?.price?.decimal,
                    commentary: e.contentitems?.find(i => i.profile === 'pinstickerlong')?.bodies?.en_GB
                }))
            });
            setInsight(response.data);
        } catch (error) {
            toast.error('Failed to generate insight');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="ai-insight-card" data-testid="ai-insight-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="h-5 w-5 text-primary" />
                    AI Race Analysis
                </CardTitle>
            </CardHeader>
            <CardContent>
                {insight ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                        <p className="whitespace-pre-wrap">{insight.preview}</p>
                        {insight.generated_at && (
                            <p className="text-xs text-muted-foreground mt-4">
                                Generated {format(parseISO(insight.generated_at), 'HH:mm')}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-muted-foreground mb-4">
                            Get a beginner-friendly breakdown of this race
                        </p>
                        <Button 
                            onClick={generateInsight} 
                            disabled={loading}
                            className="btn-pill gap-2"
                            data-testid="generate-insight-btn"
                        >
                            <Sparkles className="h-4 w-4" />
                            {loading ? 'Analyzing...' : 'Generate Analysis'}
                        </Button>
                        {!userSettings?.openai_api_key_set && (
                            <p className="text-xs text-muted-foreground mt-3">
                                Add your OpenAI API key in{' '}
                                <Link to="/settings" className="underline">Settings</Link>
                                {' '}to enable AI insights
                            </p>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// Beginner's Guide Alert
const BeginnerGuide = ({ going, raceClass }) => (
    <Alert className="mb-6 border-primary/30 bg-primary/5">
        <Lightbulb className="h-4 w-4" />
        <AlertTitle>Quick Tips for This Race</AlertTitle>
        <AlertDescription className="mt-2 space-y-2 text-sm">
            <p>
                <strong>Ground ({going}):</strong> {
                    going?.toLowerCase().includes('heavy') || going?.toLowerCase().includes('soft')
                        ? "Soft/heavy ground suits horses with stamina. Look for horses that have won on similar ground before."
                        : going?.toLowerCase().includes('firm') || going?.toLowerCase().includes('good')
                            ? "Good/firm ground favours faster horses. Check form figures for wins on quicker ground."
                            : "Check each horse's form on similar ground conditions."
                }
            </p>
            <p>
                <strong>Class {raceClass}:</strong> {
                    parseInt(raceClass) <= 2 
                        ? "High-class race with quality horses. The favourite is usually competitive."
                        : parseInt(raceClass) >= 5
                            ? "Lower-class race - upsets are more common. Consider the each-way value."
                            : "Mid-level race. Form and fitness are key factors to consider."
                }
            </p>
        </AlertDescription>
    </Alert>
);

// Race Navigation Component
const RaceNavigator = ({ meetingRaces, currentRaceId, onSelect }) => {
    const sortedRaces = useMemo(() => {
        return [...meetingRaces].sort((a, b) => new Date(a.start) - new Date(b.start));
    }, [meetingRaces]);

    const currentIndex = sortedRaces.findIndex(r => r.id === currentRaceId);
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < sortedRaces.length - 1;

    const handlePrev = () => {
        if (canGoPrev) onSelect(sortedRaces[currentIndex - 1].id);
    };

    const handleNext = () => {
        if (canGoNext) onSelect(sortedRaces[currentIndex + 1].id);
    };

    if (sortedRaces.length <= 1) return null;

    return (
        <Card className="mb-6" data-testid="race-navigator">
            <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4">
                    {/* Previous Arrow */}
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handlePrev}
                        disabled={!canGoPrev}
                        className="shrink-0"
                        data-testid="prev-race-btn"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </Button>

                    {/* Race Times */}
                    <div className="flex-1 overflow-x-auto">
                        <div className="flex items-center justify-center gap-2 min-w-max">
                            {sortedRaces.map((r, idx) => {
                                const isFinished = r.status?.includes('WEIGHED_IN') || r.status?.includes('RESULT');
                                const isCurrent = r.id === currentRaceId;
                                
                                return (
                                    <Button
                                        key={r.id}
                                        variant={isCurrent ? "default" : "ghost"}
                                        size="sm"
                                        onClick={() => onSelect(r.id)}
                                        className={`font-data transition-all ${
                                            isCurrent 
                                                ? 'bg-primary text-primary-foreground' 
                                                : isFinished 
                                                    ? 'text-muted-foreground' 
                                                    : 'hover:bg-muted'
                                        }`}
                                        data-testid={`race-time-${r.id}`}
                                    >
                                        <span className="flex flex-col items-center">
                                            <span className={`text-sm font-semibold ${isFinished && !isCurrent ? 'line-through opacity-60' : ''}`}>
                                                {format(parseISO(r.start), 'HH:mm')}
                                            </span>
                                            {isFinished && (
                                                <span className="text-[9px] uppercase opacity-70">Result</span>
                                            )}
                                        </span>
                                    </Button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Next Arrow */}
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handleNext}
                        disabled={!canGoNext}
                        className="shrink-0"
                        data-testid="next-race-btn"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </Button>
                </div>

                {/* Race Number Indicator */}
                <p className="text-center text-sm text-muted-foreground mt-2">
                    Race {currentIndex + 1} of {sortedRaces.length}
                </p>
            </CardContent>
        </Card>
    );
};

// ── Clarity Tab ───────────────────────────────────────────────────────────────

// ATR-style filled-block bar
const BLOCKS = 8;
function BlockBar({ score, label }) {
    const filled = Math.round((score / 100) * BLOCKS);
    const colour = score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-amber-400' : 'bg-red-500';
    const textColour = score >= 70 ? 'text-green-500' : score >= 40 ? 'text-amber-400' : 'text-red-500';
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <p className="text-[11px] text-muted-foreground leading-none">{label}</p>
                <p className={`text-[10px] font-semibold leading-none ${textColour}`}>{score}</p>
            </div>
            <div className="flex gap-[3px]">
                {Array.from({ length: BLOCKS }, (_, i) => (
                    <div key={i} className={`h-3.5 flex-1 rounded-[3px] ${i < filled ? colour : 'bg-muted-foreground/20'}`} />
                ))}
            </div>
        </div>
    );
}

// Rule-based reasoning text
function generateReasoning(runner, going) {
    const positives = [];
    const negatives = [];
    const { scores, going_data, days_since, headgear, form_str } = runner;
    const gd = going_data || {};

    if (scores.going >= 65 && gd.runs > 0) {
        positives.push({ label: 'Going', text: `${gd.wins} win${gd.wins !== 1 ? 's' : ''} from ${gd.runs} run${gd.runs !== 1 ? 's' : ''} on ${going || 'similar'} going` });
    } else if (scores.going < 48) {
        if (!gd.runs) negatives.push({ label: 'Going', text: `No previous runs on ${going || 'this type of'} going` });
        else negatives.push({ label: 'Going', text: `${gd.wins} win${gd.wins !== 1 ? 's' : ''} from ${gd.runs} run${gd.runs !== 1 ? 's' : ''} on similar going` });
    }

    if (scores.distance >= 70) positives.push({ label: 'Distance', text: 'Proven winner at this trip' });
    else if (scores.distance < 40) negatives.push({ label: 'Distance', text: 'No wins at this distance' });

    if (scores.course >= 70) positives.push({ label: 'Course', text: 'Good course record here' });
    else if (scores.course < 40) negatives.push({ label: 'Course', text: 'No wins at this venue' });

    if (days_since != null) {
        if (scores.fitness >= 90) positives.push({ label: 'Fitness', text: `${days_since} days since last run — ideal prep` });
        else if (scores.fitness >= 75) positives.push({ label: 'Fitness', text: `${days_since} days since last run` });
        else if (scores.fitness < 40) negatives.push({ label: 'Fitness', text: `${days_since} days since last run — fitness a question` });
    }

    if (scores.form >= 75) positives.push({ label: 'Form', text: `Strong recent form${form_str ? ` (${form_str})` : ''}` });
    else if (scores.form < 40) negatives.push({ label: 'Form', text: `Poor recent form${form_str ? ` (${form_str})` : ''}` });

    if (scores.potential >= 75) positives.push({ label: 'Potential', text: 'High-class performer on ratings' });
    else if (scores.potential < 35) negatives.push({ label: 'Potential', text: 'Modest on ratings' });

    // Jockey
    const jd = runner.jockey_data || {};
    if (scores.jockey >= 70 && jd.runs > 0) {
        positives.push({ label: 'Jockey', text: `${jd.wins} win${jd.wins !== 1 ? 's' : ''} from ${jd.runs} ride${jd.runs !== 1 ? 's' : ''} (${jd.period})` });
    } else if (scores.jockey < 45 && jd.runs > 0) {
        negatives.push({ label: 'Jockey', text: `${jd.wins} win${jd.wins !== 1 ? 's' : ''} from ${jd.runs} ride${jd.runs !== 1 ? 's' : ''} (${jd.period})` });
    }

    // Trainer
    const td = runner.trainer_data || {};
    if (scores.trainer >= 70 && td.runs > 0) {
        positives.push({ label: 'Trainer', text: `${td.wins} win${td.wins !== 1 ? 's' : ''} from ${td.runs} runners (${td.period})` });
    } else if (scores.trainer < 45 && td.runs > 0) {
        negatives.push({ label: 'Trainer', text: `${td.wins} win${td.wins !== 1 ? 's' : ''} from ${td.runs} runners (${td.period})` });
    }

    if (headgear) positives.push({ label: 'Equipment', text: `${headgear} — trainer's deliberate move` });

    return { positives, negatives };
}

const FACTOR_PAIRS = [
    [{ key: 'going',     label: 'Going' },     { key: 'distance',  label: 'Distance' }],
    [{ key: 'course',    label: 'Course' },    { key: 'fitness',   label: 'Fitness' }],
    [{ key: 'form',      label: 'Form' },      { key: 'potential', label: 'Potential' }],
    [{ key: 'jockey',    label: 'Jockey' },    { key: 'trainer',   label: 'Trainer' }],
    [{ key: 'equipment', label: 'Equipment' }, null],
];

function RunnerCard({ runner, going, eventId }) {
    const [open, setOpen] = useState(false);
    const [aiView, setAiView] = useState(false);
    const [aiText, setAiText] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    const { positives, negatives } = generateReasoning(runner, going);
    const cs = runner.career_stats || {};
    const hasReasoning = positives.length > 0 || negatives.length > 0;

    const scoreBg = runner.overall >= 70 ? 'bg-green-600' : runner.overall >= 40 ? 'bg-amber-500' : 'bg-red-600';

    const handleAiView = async () => {
        if (aiText) { setAiView(v => !v); setOpen(false); return; }
        setAiLoading(true);
        setAiView(true);
        setOpen(false);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API}/racing/events/${eventId}/clarity/commentary/${runner.entrant_id}`,
                runner,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setAiText(res.data.commentary);
        } catch {
            setAiText('AI commentary could not be generated for this runner.');
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
            {/* ── Header row ── */}
            <div className="p-3 flex items-start gap-2.5">
                {/* Silk + number */}
                <div className="flex-shrink-0 flex flex-col items-center gap-1">
                    {runner.silk_url ? (
                        <img src={runner.silk_url} alt="" className="w-9 h-9 object-contain" />
                    ) : (
                        <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">{runner.number ?? '?'}</span>
                        </div>
                    )}
                    {runner.silk_url && (
                        <span className="text-[10px] font-semibold text-muted-foreground leading-none">{runner.number}</span>
                    )}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className="font-bold text-sm uppercase tracking-wide leading-tight">{runner.name}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-1">
                            <span className={`inline-flex items-center justify-center min-w-[40px] h-9 rounded-lg text-white font-bold text-sm px-1.5 ${scoreBg}`}>
                                {runner.overall}%
                            </span>
                            {typeof runner.odds === 'string' && runner.odds && (
                                <span className="text-xs font-semibold px-2 py-1 bg-muted rounded text-muted-foreground whitespace-nowrap">
                                    {runner.odds}
                                </span>
                            )}
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {runner.jockey && <><span className="font-semibold text-foreground/60">J</span> {runner.jockey}</>}
                        {runner.trainer && <> &nbsp;<span className="font-semibold text-foreground/60">T</span> {runner.trainer}</>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {runner.form_str && <span className="font-mono mr-1.5">{runner.form_str}</span>}
                        {cs.runs > 0 && <span>Runs: {cs.runs}, Wins: {cs.wins}, Pl: {cs.places}</span>}
                    </p>
                </div>
            </div>

            {/* ── Factor grid ── */}
            <div className="px-3 pb-3 space-y-2.5">
                {FACTOR_PAIRS.map((pair, pi) => (
                    <div key={pi} className="grid grid-cols-2 gap-3">
                        {pair.map((f, fi) => f
                            ? <BlockBar key={f.key} score={runner.scores[f.key] ?? 50} label={f.label} />
                            : <div key={fi} />
                        )}
                    </div>
                ))}
            </div>

            {/* ── Action bar: Reasoning + AI View ── */}
            <div className="border-t">
                <div className="flex divide-x">
                    {hasReasoning && (
                        <button
                            className="flex-1 px-3 py-2 text-xs font-medium text-primary flex items-center justify-center gap-1 hover:bg-muted/50 transition-colors"
                            onClick={() => { setOpen(v => !v); setAiView(false); }}
                        >
                            {open ? 'Hide' : 'Show'} reasoning
                            <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                    )}
                    <button
                        className="flex-1 px-3 py-2 text-xs font-medium text-violet-500 flex items-center justify-center gap-1 hover:bg-muted/50 transition-colors"
                        onClick={handleAiView}
                        disabled={aiLoading}
                    >
                        {aiLoading
                            ? <><Loader2 className="h-3 w-3 animate-spin" /> Analysing…</>
                            : <><Bot className="h-3 w-3" /> {aiView && aiText ? 'Hide AI view' : 'AI view'}</>
                        }
                    </button>
                </div>

                {/* Rule-based reasoning panel */}
                {open && !aiView && (
                    <div className="px-3 pb-3 space-y-3 border-t bg-muted/20">
                        {positives.length > 0 && (
                            <div className="pt-3">
                                <p className="text-xs font-semibold text-green-600 flex items-center gap-1 mb-1.5">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Positives
                                </p>
                                <ul className="space-y-1">
                                    {positives.map((p, i) => (
                                        <li key={i} className="text-xs">
                                            <span className="font-semibold">{p.label}:</span> {p.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {negatives.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-red-500 flex items-center gap-1 mb-1.5">
                                    <XCircle className="h-3.5 w-3.5" /> Negatives
                                </p>
                                <ul className="space-y-1">
                                    {negatives.map((n, i) => (
                                        <li key={i} className="text-xs">
                                            <span className="font-semibold">{n.label}:</span> {n.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* AI commentary panel */}
                {aiView && (
                    <div className="px-3 py-3 border-t bg-violet-500/5">
                        <p className="text-xs font-semibold text-violet-500 flex items-center gap-1 mb-2">
                            <Bot className="h-3.5 w-3.5" /> AI Expert View
                        </p>
                        {aiLoading
                            ? <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" /> Generating analysis…
                              </div>
                            : <p className="text-xs leading-relaxed text-foreground/80">{aiText}</p>
                        }
                    </div>
                )}
            </div>
        </div>
    );
}

function ClarityTab({ eventId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const token = getToken();
        axios.get(`${API}/racing/events/${eventId}/clarity`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => setData(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [eventId]);

    if (loading) return (
        <div className="p-3 space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-40 w-full rounded-xl" />)}
        </div>
    );

    if (error || !data) return (
        <div className="p-8 text-center text-muted-foreground text-sm">
            Clarity scores unavailable for this race.
        </div>
    );

    const runners = data.clarity || [];

    return (
        <div>
            {/* Legend + going badge */}
            <div className="px-3 py-2.5 border-b flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                    Clarity — 7 factors
                    <span className="flex items-center gap-0.5">
                        <span className="inline-block w-3 h-3 rounded-[2px] bg-green-500" />70+
                    </span>
                    <span className="flex items-center gap-0.5">
                        <span className="inline-block w-3 h-3 rounded-[2px] bg-amber-400" />40–69
                    </span>
                    <span className="flex items-center gap-0.5">
                        <span className="inline-block w-3 h-3 rounded-[2px] bg-red-500" />&lt;40
                    </span>
                </p>
                {data.going && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 whitespace-nowrap">
                        {data.going}
                    </span>
                )}
            </div>

            {/* Runner cards */}
            <div className="p-3 space-y-3">
                {runners.map(runner => (
                    <RunnerCard key={runner.entrant_id} runner={runner} going={data.going} eventId={eventId} />
                ))}
                {runners.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">No runners to score.</p>
                )}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RaceDetailPage() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [race, setRace] = useState(null);
    const [meetingRaces, setMeetingRaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stable, setStable] = useState([]);
    const [userSettings, setUserSettings] = useState(null);
    const [careerStats, setCareerStats] = useState({});
    const [aiPicks, setAiPicks] = useState(null);
    const [clarityEnabled, setClarityEnabled] = useState(true);
    const { isAuthenticated } = useAuth();

    // Carried over from wherever the user clicked in from (Races list, Meeting
    // page, Racing Hub) so the course name shows instantly — falls back to the
    // backend's own same-day stage lookup (race.stage.name) for a direct visit
    // or refresh, where there's no router state to read.
    const courseName = location.state?.courseName || race?.stage?.name || '';

    useEffect(() => {
        axios.get(`${API}/site-settings/public`)
            .then(res => {
                const toggles = res.data?.feature_toggles || {};
                if (toggles.clarity === false) setClarityEnabled(false);
            })
            .catch(() => {});
    }, []);

    // AI picks for this race — shown on runner cards (badges + reasoning),
    // same data the competition pick modal displays
    useEffect(() => {
        if (!eventId || !isAuthenticated) return;
        const token = getToken();
        axios.get(`${API}/ai-picks/${eventId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => { if (res.data.available) setAiPicks(res.data.data.picks); })
            .catch(() => {});
    }, [eventId, isAuthenticated]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [raceRes] = await Promise.all([
                    axios.get(`${API}/racing/events/${eventId}`),
                ]);
                setRace(raceRes.data);

                // Fetch meeting data to get all races in the same meeting
                const stageId = raceRes.data.stage?.id;
                if (stageId) {
                    try {
                        const meetingRes = await axios.get(`${API}/racing/stages/${stageId}`);
                        const allRaces = meetingRes.data.events || [];
                        setMeetingRaces(allRaces.map(e => ({
                            id: e.id,
                            start: e.start,
                            title: e.title,
                            status: e.status
                        })));
                    } catch (err) {
                        console.error('Failed to fetch meeting races:', err);
                    }
                }

                // Fetch career stats using batch endpoint (much more efficient)
                const entrants = raceRes.data.entrants || [];
                if (entrants.length > 0) {
                    const entrantIds = entrants.slice(0, 15).map(e => e.id);
                    try {
                        const statsRes = await axios.post(`${API}/racing/entrants/batch-stats`, entrantIds);
                        setCareerStats(statsRes.data);
                    } catch (err) {
                        console.error('Failed to fetch career stats:', err);
                    }
                }

                if (isAuthenticated) {
                    const [stableRes, settingsRes] = await Promise.all([
                        axios.get(`${API}/stable`),
                        axios.get(`${API}/settings`)
                    ]);
                    setStable(stableRes.data.items || []);
                    setUserSettings(settingsRes.data);
                }
            } catch (error) {
                console.error('Failed to fetch race:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [eventId, isAuthenticated]);

    const handleRaceSelect = (raceId) => {
        navigate(`/race/${raceId}`, { state: { courseName } });
    };

    const handleAddToStable = async (item) => {
        if (!isAuthenticated) {
            toast.error('Please login to add to stable');
            return;
        }

        const { id, name, type } = item;
        
        // For jockeys/trainers, check by name since they don't have unique IDs
        const isInStable = type === 'horse' 
            ? stable.some(s => s.item_id === id && s.item_type === 'horse')
            : stable.some(s => s.item_name === name && s.item_type === type);
        
        if (isInStable) {
            const stableItem = type === 'horse'
                ? stable.find(s => s.item_id === id && s.item_type === 'horse')
                : stable.find(s => s.item_name === name && s.item_type === type);
            try {
                await axios.delete(`${API}/stable/${stableItem.id}`);
                setStable(prev => prev.filter(s => s.id !== stableItem.id));
                toast.success(`Removed ${name} from stable`);
            } catch (error) {
                toast.error('Failed to remove from stable');
            }
        } else {
            try {
                const response = await axios.post(`${API}/stable`, {
                    item_type: type,
                    item_id: id,
                    item_name: name
                });
                setStable(prev => [...prev, response.data.item]);
                const typeLabel = type === 'horse' ? '🐎' : type === 'jockey' ? '🏇' : '👔';
                toast.success(`${typeLabel} Added ${name} to stable`);
            } catch (error) {
                if (error.response?.status === 400) {
                    toast.info(`${name} is already in your stable`);
                } else {
                    toast.error('Failed to add to stable');
                }
            }
        }
    };

    // Create tip lookup
    const tipLookup = {};
    race?.tips?.forEach(tip => {
        const entrantName = tip.entrant?.name;
        if (entrantName) {
            tipLookup[entrantName] = tip.type;
        }
    });

    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-8 w-64 mb-4" />
                <Skeleton className="h-48 w-full mb-4" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!race) {
        return (
            <div className="container-app py-8 text-center">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h2 className="font-heading text-xl font-semibold">Race not found</h2>
            </div>
        );
    }

    const meta = race.meta || {};
    const going = formatGoing(meta['event:going']?.value);
    const distance = formatDistance(meta);
    const raceClass = meta['event:class']?.value || 'Unknown';
    const prize = race.prizes?.find(p => p.type === 'event:prize:place:1');
    const prizeDisplay = prize ? `£${(prize.value / 100).toLocaleString()}` : 'TBC';
    const runners = race.entrants || [];

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            <div className="container-app py-8">
                {/* Back Button */}
                <Link to="/races" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-6">
                    <ChevronLeft className="h-4 w-4" />
                    Back to Races
                </Link>

                {/* Race Navigator */}
                {meetingRaces.length > 1 && (
                    <RaceNavigator 
                        meetingRaces={meetingRaces}
                        currentRaceId={eventId}
                        onSelect={handleRaceSelect}
                    />
                )}

                {/* Race Header */}
                <div className="mb-6">
                    {courseName && (
                        <p className="text-sm font-semibold text-primary uppercase tracking-wide mb-1">
                            {courseName}
                        </p>
                    )}
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <Badge variant="outline" className="font-data text-lg px-3 py-1">
                            {format(parseISO(race.start), 'HH:mm')}
                        </Badge>
                        <RaceStatusBadge status={race.status} startTime={race.start} />
                        <Badge variant="secondary">Class {raceClass}</Badge>
                    </div>
                    <h1 className="font-heading text-2xl md:text-3xl font-bold mb-4 page-title">{race.title}</h1>
                    
                    {/* Race Info Pills */}
                    <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
                            <MapPin className="h-4 w-4 text-primary" />
                            <GlossaryTooltip term="Going">
                                <span>{going}</span>
                            </GlossaryTooltip>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
                            <Timer className="h-4 w-4 text-primary" />
                            <span>{distance}</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
                            <Trophy className="h-4 w-4 text-accent" />
                            <span>{prizeDisplay}</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
                            <User className="h-4 w-4 text-primary" />
                            <span>{runners.length} runners</span>
                        </div>
                    </div>
                </div>

                {/* Quick Results - Only shown for finished races */}
                <QuickResultsCard race={race} entrants={runners} />

                {/* Beginner Guide - Only show for upcoming races */}
                {!race.status?.includes('WEIGHED_IN') && !race.status?.includes('RESULT') && (
                    <BeginnerGuide going={going} raceClass={raceClass} />
                )}

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Runners List */}
                    <div className="lg:col-span-8">
                        <Tabs defaultValue="runners" className="w-full">
                            <TabsList className="mb-4">
                                <TabsTrigger value="runners" data-testid="tab-runners">Runners</TabsTrigger>
                                <TabsTrigger value="stats" data-testid="tab-stats">Quick Stats</TabsTrigger>
                                {clarityEnabled && (
                                    <TabsTrigger value="clarity" data-testid="tab-clarity" className="flex items-center gap-1.5">
                                        <img
                                            src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png"
                                            alt=""
                                            className="h-3.5 w-auto object-contain"
                                        />
                                        Clarity
                                    </TabsTrigger>
                                )}
                            </TabsList>
                            
                            <TabsContent value="runners">
                                <Card>
                                    <CardContent className="p-0">
                                        {runners.length === 0 ? (
                                            <div className="p-8 text-center">
                                                <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                                <h3 className="font-heading text-lg font-semibold mb-2">Runners Not Yet Declared</h3>
                                                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                                                    The field for this race hasn't been confirmed yet. 
                                                    Declarations are usually made the day before racing. 
                                                    Check back closer to race time for the full runner list.
                                                </p>
                                            </div>
                                        ) : (
                                            runners.map((runner, idx) => (
                                                <div 
                                                    key={runner.id} 
                                                    className="animate-fade-in"
                                                    style={{ animationDelay: `${idx * 50}ms` }}
                                                >
                                                    <RunnerRow
                                                        runner={runner}
                                                        eventInfo={race}
                                                        stageName={race.venue || 'Race'}
                                                        onAddToStable={handleAddToStable}
                                                        stableItems={stable}
                                                        tipType={tipLookup[runner.name]}
                                                        careerStats={careerStats[runner.id]}
                                                        aiPicks={aiPicks}
                                                    />
                                                </div>
                                            ))
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                            
                            <TabsContent value="stats">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            At a Glance
                                            <Badge variant="outline" className="ml-2">Simplified</Badge>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b">
                                                        <th className="text-left p-2 font-medium">Horse</th>
                                                        <th className="text-center p-2 font-medium">
                                                            <GlossaryTooltip term="OR">OR</GlossaryTooltip>
                                                        </th>
                                                        <th className="text-center p-2 font-medium hidden sm:table-cell">
                                                            <GlossaryTooltip term="ADJ">Adj</GlossaryTooltip>
                                                        </th>
                                                        <th className="text-center p-2 font-medium hidden md:table-cell">
                                                            <GlossaryTooltip term="TS">TS</GlossaryTooltip>
                                                        </th>
                                                        <th className="text-center p-2 font-medium">
                                                            <GlossaryTooltip term="Form">Form</GlossaryTooltip>
                                                        </th>
                                                        <th className="text-center p-2 font-medium hidden sm:table-cell">
                                                            <GlossaryTooltip term="Weight">Wgt</GlossaryTooltip>
                                                        </th>
                                                        <th className="text-center p-2 font-medium">Odds</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {runners.map(runner => {
                                                        const m = runner.meta || {};
                                                        const odds = runner.betting?.shows?.[0] || runner.betting?.forecast;
                                                        const weightPounds = m['entrant:weight:allocation:pounds']?.value;
                                                        const wgt = weightPounds 
                                                            ? `${Math.floor(weightPounds/14)}-${weightPounds%14}`
                                                            : (m['entrant:weight']?.value || '-');
                                                        const formFig = m['entrant:formfigures:jumpsix']?.figures 
                                                            || m['entrant:formfigures:flatsix']?.figures
                                                            || m['entrant:form']?.value;
                                                        const or = runner.ratings?.find(r => r.code === 'entrant:rating:official')?.value
                                                            || runner.ratings?.find(r => r.code === 'entrant:rating:or')?.value
                                                            || m['entrant:or']?.value || '-';
                                                        const adj = runner.ratings?.find(r => r.code === 'entrant:rating:adjusted')?.value || '-';
                                                        const ts = runner.ratings?.find(r => r.code === 'entrant:rating:timeform:star')?.value || null;
                                                        const isNR = runner.status === 'NON_RUNNER' || runner.status === 'DOUBTFUL';
                                                        return (
                                                            <tr key={runner.id} className={`border-b last:border-0 hover:bg-muted/50 ${isNR ? 'opacity-50' : ''}`}>
                                                                <td className="p-2 font-medium">
                                                                    {runner.name}
                                                                    {tipLookup[runner.name] && (
                                                                        <ExpertTipBadge tipType={tipLookup[runner.name]} />
                                                                    )}
                                                                </td>
                                                                <td className="p-2 text-center font-data">{or}</td>
                                                                <td className="p-2 text-center font-data hidden sm:table-cell">
                                                                    <span className={
                                                                        adj !== '-' && or !== '-' && parseInt(adj) > parseInt(or) 
                                                                            ? 'text-emerald-500' 
                                                                            : adj !== '-' && or !== '-' && parseInt(adj) < parseInt(or) 
                                                                                ? 'text-red-400' 
                                                                                : ''
                                                                    }>
                                                                        {adj}
                                                                    </span>
                                                                </td>
                                                                <td className="p-2 text-center hidden md:table-cell">
                                                                    {ts ? <TimeformStars rating={ts} /> : '-'}
                                                                </td>
                                                                <td className="p-2 text-center">
                                                                    <FormString form={formFig} />
                                                                </td>
                                                                <td className="p-2 text-center font-data hidden sm:table-cell">{wgt}</td>
                                                                <td className="p-2 text-center font-data font-semibold text-accent">
                                                                    {isNR ? 'N/R' : (odds?.price ? `${odds.price.numerator}/${odds.price.denominator}` : '-')}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Form Legend */}
                                        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                                            <p className="text-xs font-medium mb-2">Understanding Form:</p>
                                            <div className="flex flex-wrap gap-3 text-xs">
                                                <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-primary text-primary-foreground flex items-center justify-center text-[10px]">1</span> Won</span>
                                                <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-primary/70 text-primary-foreground flex items-center justify-center text-[10px]">2</span> 2nd</span>
                                                <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-primary/50 text-primary-foreground flex items-center justify-center text-[10px]">3</span> 3rd</span>
                                                <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-destructive text-destructive-foreground flex items-center justify-center text-[10px]">F</span> Fell</span>
                                                <span className="flex items-center gap-1"><span className="w-4 h-4 rounded bg-muted text-muted-foreground flex items-center justify-center text-[10px]">0</span> 10th+</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {clarityEnabled && (
                                <TabsContent value="clarity">
                                    <Card className="overflow-hidden">
                                        <CardContent className="p-0">
                                            <ClarityTab eventId={eventId} />
                                        </CardContent>
                                    </Card>
                                </TabsContent>
                            )}
                        </Tabs>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-4 space-y-6">
                        {/* Expert vs AI Picks - Side by Side */}
                        <ExpertsVsAICard tips={race.tips} entrants={runners} eventId={eventId} />
                    </div>
                </div>
            </div>
        </div>
    );
}
